import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, TrendingUp, TrendingDown, Wrench, Activity,
  RefreshCw, Search, Calendar, ArrowUpRight, Clock, Star,
  MapPin, X, Globe
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebToolsUsage = ({ fetchWithAuth }) => {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tools, setTools] = useState(null);
  const [trends, setTrends] = useState(null);
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [usersPage, setUsersPage] = useState(1);
  const [locationModal, setLocationModal] = useState(null);
  const USERS_PER_PAGE = 10;

  const fetchStats = useCallback(async (d = days) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/web-tools/stats?days=${d}`);
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error('web-tools stats:', e); }
  }, [fetchWithAuth, days]);

  const fetchTools = useCallback(async (d = days) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/web-tools/usage-by-tool?days=${d}`);
      if (res.ok) setTools(await res.json());
    } catch (e) { console.error('web-tools by-tool:', e); }
  }, [fetchWithAuth, days]);

  const fetchTrends = useCallback(async (d = days) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/web-tools/trends?days=${d}`);
      if (res.ok) setTrends(await res.json());
    } catch (e) { console.error('web-tools trends:', e); }
  }, [fetchWithAuth, days]);

  const fetchUsers = useCallback(async (d = days) => {
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/admin/web-tools/users?days=${d}`);
      if (res.ok) setUsers(await res.json());
    } catch (e) { console.error('web-tools users:', e); }
  }, [fetchWithAuth, days]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchStats(days), fetchTools(days), fetchTrends(days), fetchUsers(days)]);
      if (mounted) setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, [days]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchTools(), fetchTrends(), fetchUsers()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  // Client-side filter + search on users list
  const filteredUsers = (users?.users || []).filter(u => {
    const matchSearch = !search ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || u.status === statusFilter;
    return matchSearch && matchStatus;
  });
  const pagedUsers = filteredUsers.slice((usersPage - 1) * USERS_PER_PAGE, usersPage * USERS_PER_PAGE);
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading web tools data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Web Tools Usage</h2>
          <p className="text-muted-foreground mt-2">
            Active users and usage trends across all website tools
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(days)} onValueChange={v => { setDays(parseInt(v, 10)); setUsersPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Tool Users"
          value={stats?.total_users ?? '—'}
          change={stats?.users_change}
          icon={Users}
          color="blue"
          subtitle={`Last ${days} days`}
        />
        <StatCard
          title="Active Users"
          value={stats?.active_users ?? '—'}
          change={null}
          icon={Activity}
          color="green"
          subtitle="Used a tool recently"
        />
        <StatCard
          title="Total Uses"
          value={stats?.total_uses ?? '—'}
          change={stats?.uses_change}
          icon={Wrench}
          color="purple"
          subtitle="Across all tools"
        />
        <StatCard
          title="Most Popular Tool"
          value={stats?.most_popular_tool ?? '—'}
          change={null}
          icon={Star}
          color="orange"
          subtitle={`${stats?.most_popular_uses ?? 0} uses`}
          isText
        />
      </div>

      {/* Usage Trends Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-500" />
            Usage Trends — Last {days} Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!trends?.daily?.length ? (
            <EmptyState message="No trend data available yet." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={trends.daily}>
                <defs>
                  <linearGradient id="colorUses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" tick={{ fontSize: 11 }} />
                <YAxis className="text-xs" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--background)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend />
                <Area type="monotone" dataKey="uses" stroke="#3b82f6" fill="url(#colorUses)" strokeWidth={2} name="Tool Uses" />
                <Area type="monotone" dataKey="unique_users" stroke="#10b981" fill="url(#colorUsers)" strokeWidth={2} name="Unique Users" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Usage by Tool */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-purple-500" />
            Usage by Tool
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!tools?.tools?.length ? (
            <EmptyState message="No tool usage data available yet." />
          ) : (
            <>
              {/* Bar chart */}
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tools.tools} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="tool_name" type="category" tick={{ fontSize: 11 }} width={180} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--background)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="total_uses" fill="#3b82f6" name="Total Uses" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="unique_users" fill="#10b981" name="Unique Users" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Table below chart */}
              <div className="mt-4">
                <div className="grid grid-cols-12 text-xs font-medium text-muted-foreground px-3 pb-2 border-b border-border">
                  <span className="col-span-4">Tool</span>
                  <span className="col-span-2 text-right">Total Uses</span>
                  <span className="col-span-2 text-right">Unique Users</span>
                  <span className="col-span-2 text-right">Avg / User</span>
                  <span className="col-span-2 text-right">vs Last Period</span>
                </div>
                {tools.tools.map((tool, idx) => (
                  <div key={idx} className="grid grid-cols-12 items-center px-3 py-3 border-b border-border/50 hover:bg-muted/40 transition-colors">
                    <div className="col-span-4 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                      <span className="text-sm font-medium truncate">{tool.tool_name}</span>
                    </div>
                    <span className="col-span-2 text-sm text-right font-semibold">{tool.total_uses?.toLocaleString()}</span>
                    <span className="col-span-2 text-sm text-right">{tool.unique_users?.toLocaleString()}</span>
                    <span className="col-span-2 text-sm text-right text-muted-foreground">{tool.avg_per_user?.toFixed(1)}</span>
                    <span className={`col-span-2 text-sm text-right font-medium flex items-center justify-end gap-1 ${
                      tool.change_pct > 0 ? 'text-green-500' : tool.change_pct < 0 ? 'text-red-500' : 'text-muted-foreground'
                    }`}>
                      {tool.change_pct > 0 ? <TrendingUp className="w-3 h-3" /> : tool.change_pct < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                      {tool.change_pct != null ? `${tool.change_pct > 0 ? '+' : ''}${tool.change_pct.toFixed(1)}%` : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Active Users Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-green-500" />
              Active Users
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search + Filter */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={e => { setSearch(e.target.value); setUsersPage(1); }}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setUsersPage(1); }}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!users?.users?.length ? (
            <EmptyState message="No user activity data available yet." />
          ) : (
            <>
              {/* Table header */}
              <div className="grid grid-cols-12 text-xs font-medium text-muted-foreground px-3 pb-2 border-b border-border">
                <span className="col-span-3">User</span>
                <span className="col-span-3">Tools Used</span>
                <span className="col-span-2 text-right">Total Uses</span>
                <span className="col-span-1 text-right">Sessions</span>
                <span className="col-span-1 text-right">Status</span>
                <span className="col-span-1 text-right">Last Active</span>
                <span className="col-span-1 text-right">Location</span>
              </div>

              {pagedUsers.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No users match your search</div>
              ) : (
                pagedUsers.map((user, idx) => (
                  <div key={user.user_id || idx} className="grid grid-cols-12 items-center px-3 py-3 border-b border-border/50 hover:bg-muted/40 transition-colors">
                    {/* User */}
                    <div className="col-span-3">
                      <div className="text-sm font-medium truncate">{user.full_name || '—'}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    </div>

                    {/* Tools used */}
                    <div className="col-span-3 flex flex-wrap gap-1">
                      {(user.tools_used || []).slice(0, 3).map((tool, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground truncate max-w-[90px]">
                          {tool}
                        </span>
                      ))}
                      {(user.tools_used || []).length > 3 && (
                        <span className="text-xs text-muted-foreground">+{user.tools_used.length - 3}</span>
                      )}
                    </div>

                    {/* Total uses */}
                    <span className="col-span-2 text-sm font-semibold text-right">{user.total_uses?.toLocaleString() ?? '—'}</span>

                    {/* Sessions */}
                    <span className="col-span-1 text-sm text-right text-muted-foreground">{user.sessions ?? '—'}</span>

                    {/* Status */}
                    <div className="col-span-1 flex justify-end">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {user.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Last active */}
                    <div className="col-span-1 text-right">
                      <span className="text-xs text-muted-foreground">
                        {user.last_active ? formatRelativeTime(user.last_active) : '—'}
                      </span>
                    </div>

                    {/* Location button */}
                    <div className="col-span-1 flex justify-end">
                      <button
                        onClick={() => setLocationModal(user)}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="View location"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {usersPage} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                      disabled={usersPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUsersPage(p => Math.min(totalPages, p + 1))}
                      disabled={usersPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Location Modal */}
      {locationModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setLocationModal(null)}
        >
          <div
            className="bg-background border border-border rounded-xl shadow-xl w-80 p-5"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Globe className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground leading-tight">
                    {locationModal.full_name || 'Anonymous User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate max-w-[180px]">
                    {locationModal.email || locationModal.visitor_id || '—'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setLocationModal(null)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Location details */}
            <div className="space-y-2.5">
              <LocationRow label="IP Address" value={locationModal.ip_address} />
              <LocationRow label="City" value={locationModal.city} />
              <LocationRow label="State / Province" value={locationModal.state} />
              <LocationRow label="Country" value={locationModal.country} />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// --- Sub-components ---

const StatCard = ({ title, value, change, icon: Icon, color, subtitle, isText = false }) => {
  const colorClasses = {
    blue:   'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    green:  'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  };

  const isPositive = change > 0;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1 min-w-0">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className={`font-bold text-foreground ${isText ? 'text-base leading-snug' : 'text-3xl'}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {change != null ? (
              <div className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{Math.abs(change).toFixed(1)}% vs last period</span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ml-3 ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const LocationRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-foreground">{value || '—'}</span>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
    <Wrench className="w-10 h-10 opacity-25" />
    <p className="text-sm">{message}</p>
    <p className="text-xs">Ensure the backend endpoints are deployed and the website tracking snippet is active.</p>
  </div>
);

// Format ISO timestamp as relative time (e.g. "2h ago", "3 days ago")
const formatRelativeTime = (iso) => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return '—';
  }
};

export default WebToolsUsage;
